import { apiClient } from "./client"
import { getActiveBets, normalizeUnixTimestampToMs } from "./types/pvpRoulette"
import type {
  RoundSnapshot,
  SettlementGift,
  SettlementWinner,
} from "./types/pvpRoulette"

/** GET /v1/games/pvp-roulette/room/{roomName}/current-round */

function hasVersionAndId(data: unknown): data is Record<string, unknown> {
  if (!data || typeof data !== "object") return false
  const d = data as Record<string, unknown>
  const id = d.id
  return (
    typeof d.version === "number" && typeof id === "string" && id.length > 0
  )
}

export function mapRoundDtoToSnapshot(
  dto: Record<string, unknown>
): RoundSnapshot {
  const getStr = (k: string) =>
    typeof dto[k] === "string" ? (dto[k] as string) : undefined
  const getNum = (k: string) =>
    typeof dto[k] === "number" ? (dto[k] as number) : undefined
  const getTs = (k: string): number | null => {
    const v = dto[k]
    if (typeof v === "number" && Number.isFinite(v))
      return normalizeUnixTimestampToMs(v)
    if (typeof v === "string") {
      const n = Number(v)
      if (Number.isFinite(n)) return normalizeUnixTimestampToMs(n)
    }
    return null
  }

  const roundId = getStr("id") ?? ""
  const status = (getStr("status") ?? "WAITING") as RoundSnapshot["status"]

  const parties = (dto.parties as Record<string, unknown>[] | undefined) ?? []
  const bets = (dto.bets as Record<string, unknown>[] | undefined) ?? []
  const tricksRaw = (dto.tricks as Record<string, unknown>[] | undefined) ?? []

  const roomConfig = dto.room_config as Record<string, unknown> | undefined
  const roomCfgNum = (key: string): number | undefined => {
    if (!roomConfig) return undefined
    const value = roomConfig[key]
    return typeof value === "number" ? value : undefined
  }
  const roomCfgArr = (key: string): string[] => {
    if (!roomConfig) return []
    const value = roomConfig[key]
    return Array.isArray(value) ? value.map(String) : []
  }

  const timing: RoundSnapshot["timing"] = {
    bettingStartedAt: getTs("betting_started_at"),
    bettingEndsAt: getTs("betting_ends_at"),
    spinningStartedAt: getTs("spinning_started_at"),
    spinningEndsAt: getTs("spinning_ends_at"),
    tricksStartedAt: getTs("tricks_started_at"),
    tricksEndsAt: getTs("tricks_ends_at"),
    slowingStartedAt: getTs("slowing_started_at"),
    slowingEndsAt: getTs("slowing_ends_at"),
  }

  const tricks: RoundSnapshot["tricks"] = tricksRaw.map((t) => ({
    type: String(t.type ?? ""),
    userId: String(t.user_id ?? ""),
  }))

  const allowedTricks = roomCfgArr("allowed_tricks")
  const allowedTeams = roomCfgArr("allowed_teams")
  const mappedBets = bets.map((b) => mapBetDto(b))
  const activeBets = getActiveBets(mappedBets)
  const prizePoolTon = (() => {
    const fromBets = activeBets.reduce(
      (sum, b) => sum + (parseFloat(b.amount || "0") || 0),
      0
    )
    if (fromBets > 0) return fromBets.toFixed(2)
    const pool = parties.reduce(
      (sum, p) => sum + (Number(p.total_bank ?? 0) || 0),
      0
    )
    return pool > 0 ? String(pool / 1e9) : "0"
  })()

  const mappedParties = parties.map((p) => mapPartyDto(p))

  // Recalculate percent based on relative bank sizes across all parties
  const totalBank = mappedParties.reduce((s, p) => s + (p.totalBank ?? 0), 0)
  if (totalBank > 0 && mappedParties.length > 0) {
    for (const p of mappedParties) {
      p.percent = String((((p.totalBank ?? 0) / totalBank) * 100).toFixed(1))
    }
  }

  // If backend returns no parties but active bets exist, synthesize parties from bets
  // so the wheel can display sectors during BETTING phase.
  const finalParties =
    mappedParties.length > 0
      ? mappedParties
      : synthesizePartiesFromBets(activeBets)

  return {
    version: getNum("version") ?? 0,
    roundId,
    roundNum: getNum("number") ?? 0,
    status,
    mode: "SOLO",
    parties: finalParties,
    bets: mappedBets,
    tricks,
    timing,
    targetCoordinate: getNum("roulette_position") ?? 0,
    currentCoordinate: undefined,
    prizePoolTon,
    roomName: getStr("room_name"),
    bettingDurationSeconds: roomCfgNum("betting_duration_in_seconds"),
    spinningDurationSeconds: roomCfgNum("spinning_duration_in_seconds"),
    tricksDurationSeconds: roomCfgNum("tricks_duration_in_seconds"),
    minTeamBet: roomCfgNum("min_team_bet"),
    minSoloBet: roomCfgNum("min_solo_bet"),
    allowedTricks: allowedTricks.length ? allowedTricks : [],
    allowedTeams: allowedTeams.length > 0 ? allowedTeams : undefined,
    bettingStartedAt: getTs("betting_started_at"),
    bettingEndsAt: getTs("betting_ends_at"),
    spinningStartedAt: getTs("spinning_started_at"),
    spinningEndsAt: getTs("spinning_ends_at"),
    tricksStartedAt: getTs("tricks_started_at"),
    tricksEndsAt: getTs("tricks_ends_at"),
    slowingStartedAt: getTs("slowing_started_at"),
    slowingEndsAt: getTs("slowing_ends_at"),
    slowingOffsetMs: roomCfgNum("slowing_offset_ms"),
    createdAt: getTs("created_at"),
    commitHash: getStr("commit_hash"),
    clientCommitHash: getStr("client_commit_hash"),
    saltHash: getStr("salt_hash"),
    salt: getStr("salt"),
    winnerPartyId: getStr("winner_party_id"),
    winnerPartyType: getStr("winner_party_type") as "SOLO" | "TEAM" | undefined,
    roulettePosition: getNum("roulette_position"),
    settlement: mapSettlementDto(dto.settlement),
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object"

/** Keep only object entries of an array-or-not value. */
const asRecords = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? v.filter(isRecord) : []

const asString = (v: unknown): string | undefined =>
  typeof v === "string" ? v : undefined

const asNumber = (v: unknown): number | undefined =>
  typeof v === "number" ? v : undefined

function mapSettlementGift(g: Record<string, unknown>): SettlementGift {
  return {
    nftId: asString(g.nft_id) ?? "",
    tonEquivNano: asNumber(g.ton_equiv_nano) ?? 0,
    collection: asString(g.collection),
    model: asString(g.model),
    smallImageUrl: asString(g.small_image_url) ?? null,
    largeImageUrl: asString(g.large_image_url) ?? null,
  }
}

function mapSettlementWinner(w: Record<string, unknown>): SettlementWinner {
  return {
    userId: asString(w.user_id) ?? "",
    grossWinNano: asNumber(w.gross_win_amount) ?? 0,
    netWinNano: asNumber(w.net_win_amount) ?? 0,
    wonGifts: asRecords(w.won_gifts).map(mapSettlementGift),
  }
}

function mapSettlementDto(raw: unknown): RoundSnapshot["settlement"] {
  if (!isRecord(raw) || !Array.isArray(raw.winners)) return null
  return { winners: asRecords(raw.winners).map(mapSettlementWinner) }
}

function mapPartyDto(p: Record<string, unknown>): RoundSnapshot["parties"][0] {
  const id = String(p.id ?? "")
  const totalBank = Number(p.total_bank ?? 0)
  const playerBanksRaw = p.player_banks ?? []
  const parsedPlayerBanks = Array.isArray(playerBanksRaw)
    ? playerBanksRaw
        .filter(
          (item): item is Record<string, unknown> =>
            !!item && typeof item === "object"
        )
        .map((item) => {
          const ud = item.user_data as Record<string, unknown> | undefined
          return {
            userData: {
              userId: String(ud?.user_id ?? ""),
              username: (ud?.username as string | null | undefined) ?? null,
              photoUrl: (ud?.photo_url as string | null | undefined) ?? null,
            },
            bank: Number(item.bank ?? 0),
            partyShare:
              typeof item.party_share === "number"
                ? item.party_share
                : undefined,
            winProbability:
              typeof item.win_probability === "number"
                ? item.win_probability
                : undefined,
            potentialGrossWinAmount:
              typeof item.potential_gross_win_amount === "number"
                ? item.potential_gross_win_amount
                : undefined,
            potentialGrossPayoutAmount:
              typeof item.potential_gross_payout_amount === "number"
                ? item.potential_gross_payout_amount
                : undefined,
            potentialNetWinAmount:
              typeof item.potential_net_win_amount === "number"
                ? item.potential_net_win_amount
                : undefined,
            potentialNetPayoutAmount:
              typeof item.potential_net_payout_amount === "number"
                ? item.potential_net_payout_amount
                : undefined,
            grossWinAmount:
              typeof item.gross_win_amount === "number"
                ? item.gross_win_amount
                : undefined,
            grossPayoutAmount:
              typeof item.gross_payout_amount === "number"
                ? item.gross_payout_amount
                : undefined,
            netWinAmount:
              typeof item.net_win_amount === "number"
                ? item.net_win_amount
                : undefined,
            netPayoutAmount:
              typeof item.net_payout_amount === "number"
                ? item.net_payout_amount
                : undefined,
          }
        })
    : []
  const entries = parsedPlayerBanks.map(
    (item) => [item.userData.userId, item.bank] as const
  )
  const total = totalBank || entries.reduce((s, [, v]) => s + v, 0)
  const tonStr = total > 0 ? String(total / 1e9) : "0"
  const firstUserId = entries[0]?.[0] ?? id
  const count = Math.max(entries.length, total > 0 ? 1 : 0)
  const percent = count > 0 ? String((100 / count).toFixed(1)) : "0"
  const partyType = (p.type as "SOLO" | "TEAM") ?? "SOLO"
  const firstPbUser = parsedPlayerBanks[0]?.userData
  const username =
    typeof p.name === "string"
      ? p.name
      : partyType === "SOLO" && firstPbUser?.username
        ? firstPbUser.username
        : undefined
  return {
    userId: firstUserId,
    username,
    betAmount: tonStr,
    percent,
    id,
    partyId: id,
    partyType,
    totalBank: total || undefined,
    sectorFrom: typeof p.sector_from === "number" ? p.sector_from : undefined,
    sectorTo: typeof p.sector_to === "number" ? p.sector_to : undefined,
    winProbability:
      typeof p.win_probability === "number" ? p.win_probability : undefined,
    playerBanks: parsedPlayerBanks,
    potentialGrossWinAmount:
      typeof p.potential_gross_win_amount === "number"
        ? p.potential_gross_win_amount
        : undefined,
    potentialGrossPayoutAmount:
      typeof p.potential_gross_payout_amount === "number"
        ? p.potential_gross_payout_amount
        : undefined,
    potentialNetWinAmount:
      typeof p.potential_net_win_amount === "number"
        ? p.potential_net_win_amount
        : undefined,
    potentialNetPayoutAmount:
      typeof p.potential_net_payout_amount === "number"
        ? p.potential_net_payout_amount
        : undefined,
    grossWinAmount:
      typeof p.gross_win_amount === "number" ? p.gross_win_amount : undefined,
    grossPayoutAmount:
      typeof p.gross_payout_amount === "number"
        ? p.gross_payout_amount
        : undefined,
    netWinAmount:
      typeof p.net_win_amount === "number" ? p.net_win_amount : undefined,
    netPayoutAmount:
      typeof p.net_payout_amount === "number" ? p.net_payout_amount : undefined,
  }
}

function mapBetDto(b: Record<string, unknown>): RoundSnapshot["bets"][0] {
  const tonEquivNano =
    typeof b.ton_equiv_nano === "number" ? b.ton_equiv_nano : 0
  const tonStr = String(tonEquivNano / 1e9)
  const userData = b.user_data as Record<string, unknown> | null | undefined
  const nestedUserId =
    userData && typeof userData === "object" ? userData.user_id : undefined
  const username =
    userData && typeof userData === "object"
      ? (userData.username ?? null)
      : null
  const photoUrl =
    userData && typeof userData === "object"
      ? (userData.photo_url ?? null)
      : null
  const uid =
    nestedUserId != null && nestedUserId !== "" ? String(nestedUserId) : ""
  return {
    betId: String(b.id ?? ""),
    userData: {
      userId: uid,
      username: username != null ? String(username) : null,
      photoUrl: photoUrl != null ? String(photoUrl) : null,
    },
    amount: tonStr,
    currency: "ton",
    status: (b.status as RoundSnapshot["bets"][0]["status"]) ?? "ACTIVE",
    partyId: b.party_id ? String(b.party_id) : undefined,
    partyType: b.party_type as "SOLO" | "TEAM" | undefined,
    tonEquivNano,
    assetClass: b.asset_class as "TON" | "TELEGRAM_GIFT" | undefined,
    nftId: b.nft_id != null ? String(b.nft_id) : null,
    smallImageUrl:
      typeof b.small_image_url === "string" && b.small_image_url.length > 0
        ? b.small_image_url
        : null,
    createdAt: typeof b.created_at === "number" ? b.created_at : null,
    terminatedAt: typeof b.terminated_at === "number" ? b.terminated_at : null,
  }
}

/**
 * Synthesize parties from active bets when the backend doesn't populate the parties array.
 * Groups bets by userId (or partyId), computes relative percentages by amount.
 */
function synthesizePartiesFromBets(
  bets: RoundSnapshot["bets"]
): RoundSnapshot["parties"] {
  if (bets.length === 0) return []

  const groups = new Map<
    string,
    {
      userId: string
      partyId?: string
      partyType?: "SOLO" | "TEAM"
      total: number
    }
  >()
  for (const b of bets) {
    const key = b.partyId ?? b.userData.userId
    const existing = groups.get(key)
    const amount = parseFloat(b.amount || "0") || 0
    if (existing) {
      existing.total += amount
    } else {
      groups.set(key, {
        userId: b.userData.userId,
        partyId: b.partyId,
        partyType: b.partyType,
        total: amount,
      })
    }
  }

  const totalPool = Array.from(groups.values()).reduce((s, g) => s + g.total, 0)
  if (totalPool <= 0) return []

  return Array.from(groups.values()).map((g) => ({
    userId: g.userId,
    betAmount: String(g.total),
    percent: String(((g.total / totalPool) * 100).toFixed(1)),
    partyId: g.partyId,
    partyType: g.partyType,
  }))
}

export function parseRoundSnapshotPayload(data: unknown): RoundSnapshot | null {
  if (!data || typeof data !== "object") return null
  const obj = data as Record<string, unknown>
  if (obj.type !== "RoundUpdated") return null

  const round = obj.round
  if (!round || typeof round !== "object") return null
  const r = round as Record<string, unknown>
  if (!hasVersionAndId(r)) return null

  return mapRoundDtoToSnapshot(r)
}

export async function getCurrentRound(
  roomName: string
): Promise<RoundSnapshot | null> {
  const path = `/v1/games/pvp-roulette/room/${encodeURIComponent(roomName)}/current-round`
  const body = await apiClient.get<unknown>(path)
  const obj = body as Record<string, unknown> | null
  if (!obj || typeof obj !== "object") return null
  if (
    obj.type === "RoundUpdated" &&
    obj.round &&
    typeof obj.round === "object"
  ) {
    return parseRoundSnapshotPayload(obj)
  }
  if (hasVersionAndId(obj))
    return mapRoundDtoToSnapshot(obj as Record<string, unknown>)
  return null
}
